import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SlidePanel from '../shared/SlidePanel';
import SettingsPageLayout from '../settings/SettingsPageLayout';
import Invoices from '../settings/Invoices';
import CreditPurchases from '../settings/CreditPurchases';
import { brandIcon, methodBrand, isCardExpired, useBillingCards, CardDetailPanel } from '../settings/billingCards';
import BillingLocations from '../multiLocation/BillingLocations';
import useSites from '../multiLocation/useSites';
import SmsWalletTopupModal from '../marketing-v2/features/sms-composer/SmsWalletTopupModal';
import { BaileyStill } from '../agent/bailey/BaileyAvatar';
import { cachedLook } from '../agent/bailey/lookStore';
import usePricingUsage, { setUsage } from './usageStore';
import { pricingService, pricingMockActive } from '../../_services/pricing.service';
import { MESSAGES_VISIBLE } from '../../config/featureFlags';
import { openUpgrade, Tick } from './PricingBits';
import { compareRows } from './UpgradeModal';
import { baileySummary, monthlyTotal, nextBillAt } from './baileySummary';
import {
    meter, meterText, baileyQuota, ctaState, planSummary, priceList, receptionistMeters,
    upgradePriceLine, upgradeIncludes, upgradeChargeLine, isSelfEmployedView, viewerOf, cardState, unavailableCopy,
    receptionistState, receptionistPriceLine, receptionistChargeLine, overageLine, capOptionLabel, CAP_OPTIONS_PENCE,
    baileyPlusCap, upgradeErrorCopy, BAILEY_PLUS } from './usageRules';
import AddCardPanel from './AddCardPanel';
import { SpendNotices, SpendLimitRow } from './SpendLimit';
import { autoSpendState } from './spendRules';
import { formatPence, formatPrice, formatUnitPence, resetsLine, shortDate } from './money';
import { isOwnerHere, isSelfEmployedMe, useAccess } from '../permissions/access';
import { hapticError, hapticSuccess, hapticTick } from '../../_utils/haptics';
import toast from '../shared/Toast';
import './planBilling.css';

/**
 * Settings › Plan & billing (pricing, Oct 2026; Charlie: one page, in
 * Bailey's style). The owner's view of /settings/billing with
 * PRICING_V2_VISIBLE on; everyone else gets the Billing page as before.
 *
 *   1. Bailey's summary   what matters this month, in Bailey's words
 *   2. Your plan          plan, locations, seats, total; Bailey Plus
 *   3. This month         soft meter cards, one plain line each
 *   4. Message balance    £, top up, auto top-up
 *   5. Payment            cards and invoices, folded away until needed
 *   6. How pricing works
 *
 * Every number comes from GET /billing/usage (pence) or the card endpoints;
 * a block the API leaves out is not drawn.
 */

const Chevron = ({ open }) => (
    <svg className={`pb-chev${open ? ' is-open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
);

function Section({ id, title, aside, children }) {
    return (
        <section className="pb-section" aria-labelledby={`pb-h-${id}`} id={`pb-${id}`}>
            <div className="pb-section-head">
                <h3 id={`pb-h-${id}`}>{title}</h3>
                {aside}
            </div>
            {children}
        </section>
    );
}

/* ---------- 1. Bailey's summary ---------- */

/** The hero's one button, for the summary's urgent action (or none). */
export function heroActionLabel(action) {
    return { card: 'Update your card', 'add-card': 'Add a card', resume: 'Review automatic top-ups', 'top-up': 'Top up', receptionist: 'See your receptionist' }[action] || '';
}

function Hero({ usage, loading, error, salonName, owner, onRetry, onAction }) {
    const look = cachedLook();
    let body;
    if (!usage && loading) {
        body = <p className="pb-say is-waiting">Let me look at your plan…</p>;
    } else if (!usage) {
        body = (
            <>
                <p className="pb-say">I couldn’t load your plan just now. Your cards and invoices are below.</p>
                {error && <div className="pb-hero-actions"><button type="button" className="pb-btn is-secondary" onClick={onRetry}>Try again</button></div>}
            </>
        );
    } else {
        const s = baileySummary(usage, { salonName });
        // One button at most: the most urgent fix. Nothing urgent, no button;
        // the plan card below holds the upgrade, so it isn't offered twice.
        const label = heroActionLabel(s.action);
        body = (
            <>
                <p className="pb-say">{s.lines.map((l, i) => <span key={i} className="pb-say-line">{l}{i < s.lines.length - 1 ? ' ' : ''}</span>)}</p>
                {owner && label && (
                    <div className="pb-hero-actions">
                        <button type="button" className="pb-btn is-primary" onClick={() => onAction(s.action)}>{label}</button>
                    </div>
                )}
            </>
        );
        return (
            <section className={`pb-hero is-${s.tone}`} aria-label="Bailey’s summary">
                <div className="pb-hero-bailey" aria-hidden="true"><BaileyStill appearance={look || undefined} /></div>
                <div className="pb-bubble" role="status" aria-live="polite">
                    <span className="pb-bubble-name">Bailey</span>
                    {body}
                </div>
            </section>
        );
    }
    return (
        <section className="pb-hero" aria-label="Bailey’s summary">
            <div className="pb-hero-bailey" aria-hidden="true"><BaileyStill appearance={look || undefined} /></div>
            <div className="pb-bubble" role="status" aria-live="polite" aria-busy={loading}>
                <span className="pb-bubble-name">Bailey</span>
                {body}
            </div>
        </section>
    );
}

/* ---------- 2. Your plan ---------- */

function UpgradeCard({ usage, owner }) {
    const history = useHistory();
    const cta = ctaState(usage, { isOwner: owner });
    const teamPlan = !isSelfEmployedView(usage) && usage.plan && usage.plan.type === 'salon';
    const priceLine = upgradePriceLine(usage);
    const chargeLine = upgradeChargeLine(usage);
    // The price is already the bold line: don't say it twice ("+£29 a month,
    // billed monthly…" → "Billed monthly…").
    const chargeRest = chargeLine && priceLine && chargeLine.startsWith(priceLine.replace(/ per location$/, ''))
        ? chargeLine.slice(priceLine.replace(/ per location$/, '').length).replace(/^[,.]\s*/, '').replace(/^./, (c) => c.toUpperCase())
        : chargeLine;
    const up = (usage.plan && usage.plan.baileyUpgrade) || {};
    const rows = compareRows(usage).plus.slice(0, 3);
    if (cta.upgraded) {
        return (
            <div className="pb-upgrade is-on">
                <div className="pb-upgrade-top">
                    <span className="pb-upgrade-mark" aria-hidden="true"><Tick size={14} /></span>
                    <div>
                        <b>{BAILEY_PLUS} is on</b>
                        <span>{[priceLine && priceLine.replace(/^\+/, ''), up.since && `since ${shortDate(up.since)}`].filter(Boolean).join(', ')}</span>
                    </div>
                    {cta.canManage && <button type="button" className="pb-btn is-secondary" onClick={() => { hapticTick(); openUpgrade({ mode: 'manage', from: 'billing-plan' }); }}>Manage</button>}
                </div>
                {owner && teamPlan && history && (
                    <div className="pb-upgrade-team">
                        <span>Bailey is off for your employed team until you switch it on for them.</span>
                        <button type="button" className="pb-link" onClick={() => { hapticTick(); history.push('/staff/permissions', { openBaileyTeam: true }); }}>Choose who gets Bailey</button>
                    </div>
                )}
            </div>
        );
    }
    return (
        <div className="pb-upgrade">
            <div className="pb-upgrade-head">
                <span className="pb-upgrade-face" aria-hidden="true"><BaileyStill appearance={cachedLook() || undefined} face /></span>
                <div>
                    <b>{BAILEY_PLUS}</b>
                    <span>{isSelfEmployedView(usage)
                        ? `Up to ${baileyPlusCap(usage).toLocaleString('en-GB')} questions a month, just for you. Your clients and your money stay yours.`
                        : `Ask me up to ${baileyPlusCap(usage).toLocaleString('en-GB')} questions a month${usage.plan && usage.plan.type === 'salon' ? ', and switch me on for your team' : ''}.`}</span>
                </div>
            </div>
            <ul className="pb-upgrade-list">
                {rows.map((r) => <li key={r.text}><Tick size={14} /><span>{r.text}</span></li>)}
            </ul>
            <div className="pb-upgrade-foot">
                <div className="pb-upgrade-price">
                    {priceLine && <strong>{priceLine}</strong>}
                    {chargeRest && !cta.noPlan && <span>{chargeRest}</span>}
                </div>
                {cta.canUpgrade && <button type="button" className="pb-btn is-primary" onClick={() => { hapticTick(); openUpgrade({ from: 'billing-plan' }); }}>Get {BAILEY_PLUS}</button>}
                {cta.askOwner && <span className="pb-muted">Ask the owner about {BAILEY_PLUS}.</span>}
                {cta.noPlan && <span className="pb-muted">{unavailableCopy(usage)}</span>}
            </div>
        </div>
    );
}

function PlanSection({ usage, owner, multi, salonName }) {
    if (isSelfEmployedView(usage)) {
        // Their own terms only: no salon price, total, locations or numbers.
        const next = shortDate(nextBillAt(usage));
        const total = monthlyTotal(usage);
        return (
            <Section id="plan" title="Your plan">
                <div className="pb-card">
                    <div className="pb-plan-top">
                        <div>
                            <span className="pb-plan-name">Self-employed{salonName ? ` at ${salonName}` : ''}</span>
                            <span className="pb-plan-price">Your clients and your money stay yours.</span>
                        </div>
                        {total != null && <div className="pb-plan-total"><strong>{formatPrice(total)}</strong><span>a month</span></div>}
                    </div>
                    {next && <ul className="pb-plan-lines"><li>Next bill {next}</li></ul>}
                </div>
                <UpgradeCard usage={usage} owner={owner} />
            </Section>
        );
    }
    const plan = planSummary(usage);
    const total = monthlyTotal(usage);
    const next = shortDate(nextBillAt(usage));
    const yearly = usage.plan && usage.plan.interval === 'year';
    const n = usage.numbers;
    const numbersLine = n && (Number(n.active) || 0) > 0
        ? `${n.active} phone ${Number(n.active) === 1 ? 'number' : 'numbers'}${Number(n.included) ? ` (${n.included} included)` : ''}`
        : '';
    return (
        <Section id="plan" title="Your plan">
            <div className="pb-card">
                <div className="pb-plan-top">
                    <div>
                        <span className="pb-plan-name">{plan.name}</span>
                        {plan.price && <span className="pb-plan-price">{plan.price}{yearly ? ', billed yearly' : ''}</span>}
                    </div>
                    {total != null && (
                        <div className="pb-plan-total">
                            <strong>{formatPrice(total)}</strong>
                            <span>{multi ? 'a month, this location' : 'a month'}</span>
                        </div>
                    )}
                </div>
                <ul className="pb-plan-lines">
                    {plan.seats && <li>{plan.seats}</li>}
                    {numbersLine && <li>{numbersLine}</li>}
                    {next && <li>Next bill {next}</li>}
                    {plan.legacy && <li>You keep 2026 prices on texts and reminders.</li>}
                </ul>
                {multi && (
                    <div className="pb-locations">
                        <span className="pb-sub-h">Locations on your plan</span>
                        <BillingLocations bare />
                    </div>
                )}
            </div>
            <UpgradeCard usage={usage} owner={owner} />
        </Section>
    );
}

/* ---------- 3. This month ---------- */

function MeterCard({ label, m, unit, value, line, resets, srText }) {
    return (
        <div className={`pb-meter is-${m.level}`}>
            <span className="pb-meter-label">{label}</span>
            <span className="pb-meter-value">{value || (m.unlimited ? 'Unlimited' : <><strong>{m.used.toLocaleString('en-GB')}</strong> of {m.allowance.toLocaleString('en-GB')}</>)}</span>
            {!m.unlimited && (
                <div className="pb-bar" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={m.pct} aria-valuetext={srText || meterText(m, unit)}>
                    <span style={{ width: `${Math.max(m.pct, m.used > 0 ? 3 : 0)}%` }} />
                </div>
            )}
            {line && <p className="pb-meter-line">{line}</p>}
            {resets && <p className="pb-meter-resets">{resets}</p>}
        </div>
    );
}

function RecMeters({ minutes, replies }) {
    return (
        <div className="pb-rec-meters">
            <div>
                <span className="pb-meter-value"><strong>{minutes.used.toLocaleString('en-GB')}</strong> of {minutes.unlimited ? 'unlimited' : minutes.allowance.toLocaleString('en-GB')} minutes</span>
                {!minutes.unlimited && <div className="pb-bar" role="progressbar" aria-label="Call minutes" aria-valuemin={0} aria-valuemax={100} aria-valuenow={minutes.pct} aria-valuetext={meterText(minutes, 'minutes')}><span style={{ width: `${minutes.pct}%` }} /></div>}
            </div>
            <div>
                <span className="pb-meter-value"><strong>{replies.used.toLocaleString('en-GB')}</strong> of {replies.unlimited ? 'unlimited' : replies.allowance.toLocaleString('en-GB')} replies</span>
                {!replies.unlimited && <div className="pb-bar" role="progressbar" aria-label="Message replies" aria-valuemin={0} aria-valuemax={100} aria-valuenow={replies.pct} aria-valuetext={meterText(replies, 'replies')}><span style={{ width: `${replies.pct}%` }} /></div>}
            </div>
        </div>
    );
}

/**
 * The AI receptionist add-on (pricing 2026-10-07): try it free (once per
 * business), add it, set the monthly limit on pay as you go, turn it off.
 * Adding or turning off asks once, inline, with what it charges.
 */
export function ReceptionistCard({ usage, owner }) {
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState('');
    const rs = receptionistState(usage);
    const inc = upgradeIncludes(usage);
    const run = async (what, fn, done) => {
        setBusy(what); setError('');
        try {
            const next = await fn();
            if (next) setUsage(next);
            hapticSuccess();
            if (done) toast.success(done);
            setConfirm((c) => (what === 'cap' ? c : ''));
        } catch (e) {
            hapticError();
            setError(e && e.code === 'trial_used' ? 'Your business has already had its free trial.' : upgradeErrorCopy(e).text);
        }
        setBusy('');
    };
    const start = () => run('start', () => pricingService.receptionist('start'), 'Your receptionist is on');
    const trial = () => run('trial', () => pricingService.receptionistTrial(), 'Your free trial has started');
    const stop = () => run('cancel', () => pricingService.receptionist('cancel'), 'Your receptionist is off');
    const setCap = (pence) => run('cap', () => pricingService.setReceptionistCap(pence));
    const price = receptionistPriceLine(usage, { step: true });
    const bundleNote = rs.stepPence != null && rs.pricePence != null && rs.stepPence < rs.pricePence
        ? `${formatPrice((usage.plan.bundle && usage.plan.bundle.pricePence) || 0)} for both with ${BAILEY_PLUS}` : '';
    const confirmBox = confirm === 'start' ? (
        <div className="pb-confirm" role="group" aria-label="Add the AI receptionist">
            <p><b>Add the AI receptionist?</b> {receptionistChargeLine(usage)} Cancel any time.</p>
            <div className="pb-confirm-actions">
                <button type="button" className="pb-btn is-secondary" disabled={!!busy} onClick={() => setConfirm('')}>Not now</button>
                <button type="button" className="pb-btn is-primary" disabled={!!busy} onClick={start}>{busy === 'start' ? 'Adding…' : 'Add it'}</button>
            </div>
        </div>
    ) : confirm === 'cancel' ? (
        <div className="pb-confirm" role="group" aria-label="Turn off the AI receptionist">
            <p><b>Turn off the AI receptionist?</b> It stops answering calls and messages straight away, and you get a credit for the rest of the month.</p>
            <div className="pb-confirm-actions">
                <button type="button" className="pb-btn is-secondary" disabled={!!busy} onClick={() => setConfirm('')}>Keep it</button>
                <button type="button" className="pb-btn is-danger" disabled={!!busy} onClick={stop}>{busy === 'cancel' ? 'Turning off…' : 'Turn it off'}</button>
            </div>
        </div>
    ) : null;

    if (rs.state === 'legacy') {
        return (
            <div className="pb-meter pb-teaser">
                <span className="pb-meter-label">AI receptionist</span>
                <p className="pb-meter-line">Your receptionist stays on the terms you have today.</p>
            </div>
        );
    }

    if (rs.state === 'trial') {
        return (
            <div className="pb-meter pb-rec is-ok">
                <div className="pb-rec-head">
                    <span className="pb-meter-label">AI receptionist</span>
                    <span className="pb-chip is-on">Free trial</span>
                </div>
                <RecMeters minutes={rs.trial.minutes} replies={rs.trial.replies} />
                <p className="pb-meter-line">Free until {shortDate(rs.trial.endsAt) || 'the end of your trial'}. It stops on its own unless you add it. No card needed.</p>
                {error && <p className="pb-error" role="alert">{error}</p>}
                {owner && !confirm && <div className="pb-rec-actions"><button type="button" className="pb-btn is-primary" onClick={() => { hapticTick(); setConfirm('start'); }}>Keep it{price ? ` · ${price}` : ''}</button></div>}
                {confirmBox}
            </div>
        );
    }

    if (rs.state !== 'on') {
        return (
            <div className="pb-meter pb-teaser">
                <span className="pb-meter-label">AI receptionist</span>
                <p className="pb-meter-line">I answer your calls and messages, day and night: {inc.minutes} minutes and {inc.replies} replies a month, and a phone number.</p>
                {rs.state === 'trial-ended' && <p className="pb-meter-resets">Your free trial has ended.</p>}
                {rs.trial.usedElsewhere && <p className="pb-meter-resets">Your business has had its free trial at another location.</p>}
                {price && <p className="pb-meter-resets"><b>{price}</b>{bundleNote ? ` (${bundleNote})` : ''}</p>}
                {error && <p className="pb-error" role="alert">{error}</p>}
                {owner && !rs.available && <p className="pb-meter-resets">{unavailableCopy(usage, 'receptionistAddon')}</p>}
                {owner && rs.available && !confirm && (
                    <div className="pb-rec-actions">
                        {rs.trial.available && <button type="button" className="pb-btn is-primary" disabled={!!busy} onClick={() => { hapticTick(); trial(); }}>{busy === 'trial' ? 'Starting…' : `Try it free for ${rs.trial.days} days`}</button>}
                        <button type="button" className={`pb-btn ${rs.trial.available ? 'is-secondary' : 'is-primary'}`} onClick={() => { hapticTick(); setConfirm('start'); }}>Add it</button>
                    </div>
                )}
                {rs.trial.available && !confirm && owner && <p className="pb-meter-resets">{rs.trial.minutes.allowance} minutes and {rs.trial.replies.allowance} replies, with a number. No card, and it stops on its own.</p>}
                {confirmBox}
            </div>
        );
    }

    const r = receptionistMeters(usage);
    const o = rs.overage;
    const line = o.capReached
        ? 'It’s reached your limit for this month, so it’s taking messages until the 1st.'
        : r.alertLevel >= 100 ? 'This month’s allowance is used.'
            : r.alertLevel >= 80 ? 'Most of this month’s allowance is used.' : 'Calls and messages I’ve answered for you this month.';
    return (
        <div className={`pb-meter pb-rec is-${o.capReached ? 'full' : r.alertLevel >= 80 ? 'warn' : 'ok'}`}>
            <div className="pb-rec-head">
                <span className="pb-meter-label">AI receptionist</span>
                {o.chargedPence > 0 && <span className="pb-chip">{formatPence(o.chargedPence)} extra this month</span>}
            </div>
            <RecMeters minutes={r.minutes} replies={r.replies} />
            <p className="pb-meter-line">{line}</p>
            <p className="pb-meter-resets">{o.capPence === 0 ? 'No extra use: it stops at the allowance.' : `Monthly limit ${o.capPence != null ? formatPrice(o.capPence) : ''}`.trim()}</p>
            {owner && (
                <div className="pb-rec-actions">
                    <button type="button" className="pb-btn is-secondary" onClick={() => { hapticTick(); setConfirm('manage'); }}>Manage</button>
                </div>
            )}
            {confirm === 'manage' || confirm === 'cancel' ? (
                <ReceptionistPanel usage={usage} busy={busy} error={error} confirmOff={confirm === 'cancel'}
                    onAskOff={() => setConfirm('cancel')} onKeep={() => setConfirm('manage')}
                    onOff={stop} onCap={setCap} onClose={() => { setConfirm(''); setError(''); }} />
            ) : null}
        </div>
    );
}

/**
 * The receptionist's settings in a side panel (the Marketing settings
 * pattern): this month, the monthly limit on pay as you go, turn it off.
 */
function ReceptionistPanel({ usage, busy, error, confirmOff, onAskOff, onKeep, onOff, onCap, onClose }) {
    const rs = receptionistState(usage);
    const r = receptionistMeters(usage);
    const o = rs.overage;
    const options = CAP_OPTIONS_PENCE.includes(o.capPence) || o.capPence == null ? CAP_OPTIONS_PENCE : [...CAP_OPTIONS_PENCE, o.capPence].sort((x, y) => x - y);
    return (
        <SlidePanel title="AI receptionist" width={420} onClose={onClose}>
            <div className="sp-form pb-recp">
                <span className="pb-sub-h">This month</span>
                <RecMeters minutes={r.minutes} replies={r.replies} />
                {o.chargedPence > 0 && <p className="pb-meter-resets">{formatPence(o.chargedPence)} so far past the allowance, from your balance.</p>}

                <span className="pb-sub-h">When the allowance runs out</span>
                <p className="sp-hint" style={{ marginTop: 0 }}>
                    {o.minutePence != null ? `It keeps answering at ${formatUnitPence(o.minutePence)} a minute and ${formatUnitPence(o.replyPence)} a reply from your balance, up to the limit you choose. Then it takes messages until the 1st.` : 'Choose how much extra it can use each month.'}
                </p>
                <div className="pb-recp-caps" role="radiogroup" aria-label="Monthly limit">
                    {options.map((v) => {
                        const on = o.capPence === v;
                        return (
                            <button key={v} type="button" role="radio" aria-checked={on} disabled={busy === 'cap'}
                                className={`pb-recp-cap${on ? ' is-on' : ''}`} onClick={() => { if (!on) { hapticTick(); onCap(v); } }}>
                                <span>{capOptionLabel(v)}</span>
                                <span className="pb-recp-dot" aria-hidden="true" />
                            </button>
                        );
                    })}
                </div>
                {error && <p className="pb-error" role="alert">{error}</p>}

                <div className="pb-recp-off">
                    {confirmOff ? (
                        <div className="pb-confirm" role="group" aria-label="Turn off the AI receptionist">
                            <p><b>Turn off the AI receptionist?</b> It stops answering calls and messages straight away, and you get a credit for the rest of the month.</p>
                            <div className="pb-confirm-actions">
                                <button type="button" className="pb-btn is-secondary" disabled={!!busy} onClick={onKeep}>Keep it</button>
                                <button type="button" className="pb-btn is-danger" disabled={!!busy} onClick={onOff}>{busy === 'cancel' ? 'Turning off…' : 'Turn it off'}</button>
                            </div>
                        </div>
                    ) : (
                        <button type="button" className="pb-link is-danger" onClick={() => { hapticTick(); onAskOff(); }}>Turn off the AI receptionist</button>
                    )}
                </div>
            </div>
        </SlidePanel>
    );
}


function MonthSection({ usage, owner }) {
    const rem = usage.reminders;
    const mf = usage.marketingFree;
    const bq = usage.bailey ? baileyQuota(usage.bailey) : null;
    const prices = priceList(usage);
    const remM = rem ? meter(rem.used, rem.allowance) : null;
    const over = rem && Number(rem.overageCount) > 0 ? Number(rem.overageCount) : 0;
    const mfM = mf ? meter(mf.usedPence, mf.allowancePence) : null;
    return (
        <Section id="month" title="This month">
            <div className="pb-grid">
                {remM && (
                    <MeterCard
                        label="Reminder texts"
                        m={remM}
                        unit="reminders"
                        line={over
                            ? `${over.toLocaleString('en-GB')} more sent${prices.reminderOver ? ` at ${prices.reminderOver} each` : ''}. Reminders never stop, even at £0.`
                            : 'Reminders never stop, even at £0.'}
                        resets={resetsLine(rem.resetsAt)}
                    />
                )}
                {mfM && (
                    <MeterCard
                        label="Free marketing"
                        m={mfM}
                        unit="pence"
                        srText={`${formatPence(mfM.used)} of ${formatPrice(mfM.allowance)} used`}
                        value={<><strong>{formatPence(mfM.used)}</strong> of {formatPrice(mfM.allowance)}</>}
                        line={mfM.level === 'full' ? 'Used up. Marketing now comes from your balance.' : 'Used before your balance, every month.'}
                        resets={resetsLine(mf.resetsAt)}
                    />
                )}
                {bq && (
                    <MeterCard
                        label="Questions for Bailey"
                        m={meter(bq.used, bq.unlimited ? null : bq.plus ? bq.cap : bq.allowance)}
                        unit="questions"
                        line={bq.plus
                            ? (bq.exhausted ? `That’s this month’s ${BAILEY_PLUS} questions.` : `With ${BAILEY_PLUS}.${usage.bailey.coveredBySalon ? ' Covered by the salon.' : ''}`)
                            : bq.unlimited ? 'Ask me as much as you like.'
                                : bq.exhausted ? 'That’s this month’s free questions.' : 'Free every month.'}
                        resets={bq.unlimited ? '' : resetsLine(bq.resetsAt)}
                    />
                )}
                {MESSAGES_VISIBLE && usage.directTexts && <DirectTextsCard d={usage.directTexts} />}
                {usage.plan && !isSelfEmployedView(usage) && <ReceptionistCard usage={usage} owner={owner} />}
            </div>
        </Section>
    );
}

/**
 * Texts and emails to clients from Messages and client pages (texting plan,
 * 14 Oct 2026): no allowance, straight from the balance at the usual price.
 */
function DirectTextsCard({ d }) {
    const texts = Number(d.texts) || 0;
    const emails = Number(d.emails) || 0;
    const parts = [];
    if (texts) parts.push(`${texts.toLocaleString('en-GB')} ${texts === 1 ? 'text' : 'texts'}`);
    if (emails) parts.push(`${emails.toLocaleString('en-GB')} ${emails === 1 ? 'email' : 'emails'}`);
    return (
        <MeterCard
            label="Texts to clients"
            m={meter(texts + emails, null)}
            unit="messages"
            value={parts.length ? <><strong>{formatPence(Number(d.pence) || 0)}</strong> for {parts.join(' and ')}</> : 'None yet'}
            line="Replies to clients from Messages and client pages, from your balance. Email and the Beu app cost less."
        />
    );
}

/* ---------- 4. Message balance ---------- */

function BalanceSection({ usage, owner, onTopUp, onCard, salonName, onCheckActivity }) {
    const bal = usage.balance;
    if (!bal && usage.balanceCoveredBySalon) {
        return (
            <Section id="balance" title="Message balance">
                <div className="pb-card pb-covered">
                    <b>{salonName || 'The salon'} covers your marketing</b>
                    <span className="pb-muted">Your texts and emails to clients are paid for by the salon, so there’s nothing for you to top up.</span>
                </div>
            </Section>
        );
    }
    if (!bal) return null;
    const pence = bal.pence != null ? Number(bal.pence) : null;
    const settle = bal.settlement || {};
    const auto = bal.autoTopup || {};
    const negative = pence != null && pence < 0;
    const due = settle.duePence != null ? Number(settle.duePence) : negative ? -pence : 0;
    const when = shortDate(settle.nextAt);
    const p = priceList(usage);
    return (
        <Section id="balance" title="Message balance">
            <div className="pb-card pb-balance">
                <div className="pb-balance-top">
                    <div>
                        <span className={`pb-balance-num${negative ? ' is-negative' : ''}`}>{formatPence(pence)}</span>
                        <span className="pb-muted">{MESSAGES_VISIBLE
                            ? <>For texts{p.text ? ` (${p.text})` : ''} and emails{p.email ? ` (${p.email})` : ''} to clients and for marketing, and reminders past your allowance. It never expires.</>
                            : <>For marketing{p.text ? ` texts (${p.text})` : ' texts'}{p.email ? ` and emails (${p.email})` : ' and emails'}, and reminders past your allowance. It never expires.</>}</span>
                    </div>
                    {owner && <button type="button" className="pb-btn is-primary" onClick={() => { hapticTick(); onTopUp(); }}>Top up</button>}
                </div>
                {settle.failedAt ? (
                    <div className="pb-note is-bad" role="alert">
                        I couldn’t take {formatPence(due)}. Reminders are still sending. <button type="button" className="pb-link" onClick={onCard}>Update your card</button>
                    </div>
                ) : negative && !(autoSpendState(usage) || {}).held ? (
                    <div className="pb-note is-warm">Below zero is fine: reminders keep sending. I’ll settle {formatPence(due)} on your card{when ? ` on ${when}` : ' at the end of the month'}, or top up now to clear it.</div>
                ) : null}
                <SpendNotices usage={usage} isOwner={owner} onCheckActivity={onCheckActivity} />
                <div className="pb-row">
                    <div>
                        <b>Auto top-up</b>
                        <span>{auto.enabled ? `On: below ${formatPrice(auto.thresholdPence)}, add ${formatPrice(auto.amountPence)}.` : 'Off. Turn it on so campaigns never stall.'}</span>
                    </div>
                    {owner && <button type="button" className="pb-link" onClick={onTopUp}>{auto.enabled ? 'Change' : 'Turn on'}</button>}
                </div>
                <SpendLimitRow usage={usage} isOwner={owner} />
            </div>
        </Section>
    );
}

/* ---------- 5. Payment ---------- */

function PaymentSection({ cards, open, setOpen, onAddCard, tab, setTab }) {
    const def = cards.cardList.find((c) => c.id === cards.defaultPayment) || cards.cardList[0];
    const summary = def
        ? `${methodBrand(def).replace(/^./, (c) => c.toUpperCase())}${def.card && def.card.last4 ? ` ending ${def.card.last4}` : ''} · Invoices`
        : 'No card yet · Invoices';
    return (
        <section className="pb-section" id="pb-payment" aria-labelledby="pb-h-payment">
            <h3 className="pb-fold-h" id="pb-h-payment">
                <button type="button" className="pb-fold" aria-expanded={open} aria-controls="pb-payment-body" onClick={() => { hapticTick(); setOpen(!open); }}>
                    <span className="pb-fold-text">
                        <b>Payment</b>
                        <span>{summary}</span>
                    </span>
                    <Chevron open={open} />
                </button>
            </h3>
            {open && (
                <div id="pb-payment-body" className="pb-fold-body">
                    <div className="pb-card pb-cards">
                        {cards.cardList.length === 0 && <p className="pb-muted">No card yet. Add one for your plan and for settling messages.</p>}
                        {cards.cardList.map((card) => {
                            const isDefault = cards.defaultPayment === card.id;
                            return (
                                <button type="button" key={card.id} className="pb-card-row" onClick={() => cards.setSelectedCard(card)}>
                                    <span className="pb-card-icon">{brandIcon(card.card && card.card.brand)}</span>
                                    <span className="pb-card-info">
                                        <b>{methodBrand(card)}</b>
                                        <span>{card.card && card.card.last4 ? `ending ${card.card.last4}` : (card.link && card.link.email) || 'saved payment method'}</span>
                                    </span>
                                    {isCardExpired(card) && <span className="pb-chip is-bad">Expired</span>}
                                    {isDefault && <span className="pb-chip is-on">Default</span>}
                                </button>
                            );
                        })}
                        {onAddCard && <button type="button" className="pb-btn is-secondary pb-add-card" onClick={onAddCard}>Add a card</button>}
                    </div>
                    <div className="pb-tabs" role="tablist" aria-label="Billing history">
                        <button type="button" role="tab" aria-selected={tab === 'invoices'} className={tab === 'invoices' ? 'is-on' : ''} onClick={() => setTab('invoices')}>Invoices</button>
                        <button type="button" role="tab" aria-selected={tab === 'topups'} className={tab === 'topups' ? 'is-on' : ''} onClick={() => setTab('topups')}>Top-ups</button>
                    </div>
                    <div className="pb-history">{tab === 'invoices' ? <Invoices /> : <CreditPurchases />}</div>
                </div>
            )}
        </section>
    );
}

/* ---------- 6. How pricing works ---------- */

export function HowPricingWorks({ usage, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const p = priceList(usage);
    const plan = planSummary(usage);
    const inc = upgradeIncludes(usage);
    const priceLine = upgradePriceLine(usage);
    const mf = usage.marketingFree && usage.marketingFree.allowancePence != null ? formatPrice(usage.marketingFree.allowancePence) : '';
    const se = isSelfEmployedView(usage);
    const rs = receptionistState(usage);
    const cap = baileyPlusCap(usage);
    return (
        <section className="pb-section">
            <h3 className="pb-fold-h">
                <button type="button" className="pb-fold" aria-expanded={open} aria-controls="pb-how-body" onClick={() => setOpen((v) => !v)}>
                    <span className="pb-fold-text"><b>How pricing works</b></span>
                    <Chevron open={open} />
                </button>
            </h3>
            {open && (se ? (
                <ul id="pb-how-body" className="pb-how">
                    <li>You get Bailey for {(usage.bailey && usage.bailey.allowance) || 30} questions a month, free.</li>
                    {usage.balanceCoveredBySalon
                        ? <li>The salon covers your marketing texts and emails, so there’s nothing for you to top up.</li>
                        : (p.text || p.email) && <li>Your own message balance pays for your marketing: {[p.text && `texts ${p.text}`, p.email && `emails ${p.email}`].filter(Boolean).join(', ')}.</li>}
                    <li>Reminders never stop, even if a balance runs out.</li>
                    {priceLine && <li>{BAILEY_PLUS} ({priceLine}) gives you up to {cap.toLocaleString('en-GB')} questions a month, just for you, billed monthly on your own card. Your clients and your money stay yours.</li>}
                    {usage.bailey && usage.bailey.coveredBySalon && <li>The salon’s {BAILEY_PLUS} covers you, as it runs your marketing.</li>}
                </ul>
                ) : (
                <ul id="pb-how-body" className="pb-how">
                    {plan.price && <li>{plan.name}: {plan.price}{plan.seats ? ', plus each self-employed stylist' : ''}.</li>}
                    <li>Everyone gets Bailey for {(usage.bailey && usage.bailey.allowance) || 30} questions a month{mf ? `, ${mf} of marketing messages free each month` : ''}, and an allowance of reminder texts.</li>
                    {(p.text || p.email) && <li>Your message balance pays for the rest: {[p.text && `texts ${p.text}`, p.email && `emails ${p.email}`].filter(Boolean).join(', ')}.{plan.legacy ? ' These are your 2026 prices.' : ''}</li>}
                    <li>Reminders never stop. If your balance is empty they still send, it can go below zero, and we settle up on your card once a month.</li>
                    {priceLine && <li>{BAILEY_PLUS} ({priceLine}): up to {cap.toLocaleString('en-GB')} questions a month{plan.name === 'Solo' ? '' : ', and Bailey for the team members you choose'}.</li>}
                    {rs.pricePence != null && <li>AI receptionist ({receptionistPriceLine(usage)}): {inc.minutes} minutes and {inc.replies} replies a month, and a phone number. {overageLine(usage)}{usage.plan.bundle && usage.plan.bundle.pricePence ? ` Both together: ${formatPrice(usage.plan.bundle.pricePence)} a month${plan.name === 'Solo' ? '' : ' per location'}.` : ''}</li>}
                    {rs.pricePence != null && <li>Try the receptionist free for {rs.trial.days} days, once per business. No card, and it stops on its own.</li>}
                    {usage.numbers && usage.numbers.pricePence != null && <li>Extra phone numbers are {formatPrice(usage.numbers.pricePence)} a month each.</li>}
                </ul>
                ))}
        </section>
    );
}

/* ---------- The page ---------- */

// The login snapshot's salon name, for before the sites list or the store has it.
function storedSalonName() {
    try {
        const u = JSON.parse(localStorage.getItem('user')) || {};
        const h = u.hairdresser || (u.userInfo && u.userInfo.hairdresser) || {};
        return (h.salon && h.salon.salonName) || '';
    } catch (e) { return ''; }
}

function PlanBillingPage(props) {
    useAccess();
    const { usage, error, loading, refresh } = usePricingUsage();
    // Who may change things here: the owner (the salon's plan) or a
    // self-employed stylist (their own). Staff see it read-only.
    const viewer = usage ? viewerOf(usage) : (isSelfEmployedMe() ? 'self-employed' : 'owner');
    const owner = viewer !== 'staff' && (isOwnerHere() || viewer === 'self-employed');
    const cards = useBillingCards();
    const { active, multi } = useSites();
    const reduxSalon = props.user && props.user.userInfo && props.user.userInfo.hairdresser && props.user.userInfo.hairdresser.salon;
    const salonName = (active && active.salonName) || (reduxSalon && reduxSalon.salonName) || storedSalonName();
    const wantsPayment = !!(props.location && props.location.state && props.location.state.open === 'payment');
    const [payOpen, setPayOpen] = useState(wantsPayment);
    const [payTab, setPayTab] = useState('invoices');
    const [topUp, setTopUp] = useState(false);
    const [addingCard, setAddingCard] = useState(false);
    const scrolled = useRef(false);
    // A card saved anywhere (here, or from the upgrade) shows in the list.
    const cardOnFile = usage ? cardState(usage).cardOnFile : null;
    useEffect(() => { if (cardOnFile) cards.fetchCards(); }, [cardOnFile]); // eslint-disable-line react-hooks/exhaustive-deps

    const showPayment = () => {
        setPayOpen(true);
        requestAnimationFrame(() => { const el = document.getElementById('pb-payment'); if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    };
    useEffect(() => { if (wantsPayment && !scrolled.current) { scrolled.current = true; showPayment(); } }, [wantsPayment]); // eslint-disable-line react-hooks/exhaustive-deps

    const onAction = (a) => {
        hapticTick();
        if (a === 'card') showPayment();
        else if (a === 'top-up') setTopUp(true);
        else if (a === 'add-card') setAddingCard(true);
        else if (a === 'resume') { const el = document.getElementById('pb-balance'); if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        else if (a === 'receptionist') { const el = document.getElementById('pb-month'); if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    };

    return (
        <SettingsPageLayout activeTab="billing" {...props}>
            <div className="catalogue-content-wrapper pb-page">
                <Hero usage={usage} loading={loading} error={error} salonName={salonName} owner={owner} onRetry={refresh} onAction={onAction} />
                {pricingMockActive && <p className="pb-muted pb-sample">Sample data (dev mock)</p>}
                {usage && usage.plan && <PlanSection usage={usage} owner={owner} multi={multi} salonName={salonName} />}
                {usage && <MonthSection usage={usage} owner={owner} />}
                {usage && <BalanceSection usage={usage} owner={owner} onTopUp={() => setTopUp(true)} onCard={showPayment} salonName={salonName} onCheckActivity={() => { setPayTab('topups'); showPayment(); }} />}
                <PaymentSection cards={cards} open={payOpen} setOpen={setPayOpen} tab={payTab} setTab={setPayTab} onAddCard={owner ? () => { hapticTick(); setAddingCard(true); } : null} />
                {usage && <HowPricingWorks usage={usage} />}
            </div>
            <CardDetailPanel cards={cards} />
            {addingCard && <AddCardPanel onClose={() => setAddingCard(false)} onSaved={() => { cards.fetchCards(); refresh(); }} />}
            {topUp && usage && (
                <SmsWalletTopupModal
                    balancePence={usage.balance && usage.balance.pence}
                    prices={usage.prices}
                    autoRecharge={usage.balance && usage.balance.autoTopup}
                    onSuccess={refresh}
                    onNeedCard={() => { setTopUp(false); setAddingCard(true); }}
                    onClose={() => { setTopUp(false); refresh(); }}
                />
            )}
        </SettingsPageLayout>
    );
}

export default connect((state) => ({ user: state.user }))(PlanBillingPage);
